import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Container,
  Stack,
  Paper,
  Typography,
  Link,
  Grid,
  Button,
  styled,
} from "@mui/material";

import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuthSession } from "../../../components/AuthSessionProvider";
import { Listings } from "../components";
import {
  Filters,
  Header,
  useLanguageTranslator,
  CTAContainer,
  ConditionalRenderer,
  TabsFilters,
  DataDisplayMessage,
  PersonCard,
  StatusTag,
} from "../../../components";
import {
  useLatestActions,
  useLatestPositions,
  usePeoplesCall,
} from "../../../hooks";
import { useFilters } from "../helpers";
import { gql, useMutation } from "@apollo/client";
import { useNotify } from "../../../components/@common/NotificationsProvider";

const StatusWrapper = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  backgroundColor: "#ffffff",
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  boxShadow: theme.shadows[3],
}));

const personDeleteMutationDocument = gql`
  mutation personDelete($person: PersonDeleteInput!) {
    personDelete(input: $person) {
      person {
        id
      }
    }
  }
`;

function PeopleHome() {
  const [inputValue, setInputValue] = useState("");
  const [filters, setFilters] = useState({
    country: "",
    startYear: "",
    endYear: "",
    theme: "",
  });
  const [currTab, setCurrTab] = useState("all");

  const { user } = useAuthSession() as any;
  const isLoggedIn = !!user;

  const notify = useNotify();
  const location = useLocation();
  const isPrivate = location?.pathname?.includes("/submission/people");
  const { translator } = useLanguageTranslator();

  const { data, refetch, loading } = usePeoplesCall({
    isPrivate,
    variables: {
      limit: 8,
      ...(filters?.country && filters?.country !== "0"
        ? { countryId: Number(filters?.country) }
        : {}),
      ...(filters?.theme && filters?.theme !== "0"
        ? { themeId: Number(filters?.theme) }
        : {}),
      ...(filters?.startYear ? { startYear: Number(filters?.startYear) } : {}),
      ...(filters?.endYear ? { endYear: Number(filters?.endYear) } : {}),
    },
    user,
  });

  const { people = [], dist = {} }: any = useFilters({ isPrivate, data });

  const tabsData = [
    isPrivate
      ? {}
      : {
          value: "all",
          count: data?.people?.collection?.length,
          title: translator("All"),
        },
    {
      value: "approved",
      count: dist?.approved.length,
      title: translator("status.Approved"),
    },
    {
      value: "pending",
      count: dist?.pending.length,
      title: translator("status.Pending"),
    },
    {
      value: "rejected",
      count: dist?.rejected.length,
      title: translator("status.Rejected"),
    },
  ];

  const { data: latestActions } = useLatestActions({ variables: { limit: 8 } });
  const { data: latestPositions } = useLatestPositions({
    variables: { limit: 8 },
  });

  const filteredDataResults = useMemo(() => {
    if (currTab === "all") return people;
    return people?.filter(
      (item: any) => item.status?.toLowerCase() === currTab
    );
  }, [people, currTab]);

  const modifiedData = isPrivate
    ? filteredDataResults || []
    : data?.people?.collection || [];

  const [deletePeopleMutation, { loading: deletingPeople }] = useMutation(
    personDeleteMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const handleDelete = useCallback(
    async (personId: any) => {
      if (await notify.confirm(`Deleting Person. This cannot be undone.`)) {
        try {
          await deletePeopleMutation({
            variables: { person: { id: personId } },
          });
          notify.success(`People Deleted successfully!`);
          refetch();
        } catch (err) {
          notify.error(err as Error);
        }
      }
    },
    [deletePeopleMutation, notify]
  );

  const filteredPeopleList = useMemo(() => {
    if (!inputValue) return modifiedData || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return modifiedData?.filter((item: any) => {
      const firstName = item?.firstname || "";
      const lastName = item?.lastname || "";

      const normalizedFirstName = firstName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();
      const normalizedLastName = lastName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();

      return (
        normalizedFirstName.includes(searchValue) ||
        normalizedLastName.includes(searchValue)
      );
    });
  }, [inputValue, modifiedData]);

  const filteredActionsList = useMemo(() => {
    if (!inputValue) return latestActions?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return latestActions?.collection?.filter((item: any) => {
      const firstName = item?.firstname || "";
      const lastName = item?.lastname || "";

      const normalizedFirstName = firstName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();
      const normalizedLastName = lastName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();

      return (
        normalizedFirstName.includes(searchValue) ||
        normalizedLastName.includes(searchValue)
      );
    });
  }, [inputValue, latestActions?.collection]);

  const filteredPositionsList = useMemo(() => {
    if (!inputValue) return latestPositions?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return latestPositions?.collection?.filter((item: any) => {
      const firstName = item?.firstname || "";
      const lastName = item?.lastname || "";

      const normalizedFirstName = firstName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();
      const normalizedLastName = lastName
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase();

      return (
        normalizedFirstName.includes(searchValue) ||
        normalizedLastName.includes(searchValue)
      );
    });
  }, [inputValue, latestPositions?.collection]);

  return (
    <Box pb={4}>
      <Header
        title={translator("People")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />

      <Container sx={{ my: 3 }}>
        <ConditionalRenderer condition={isPrivate}>
          <TabsFilters
            currTab={currTab}
            setCurrTab={setCurrTab}
            listItems={tabsData}
            heading={`${translator("Latest People")}`}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={!isPrivate}>
          <Paper
            component={Container}
            sx={{
              py: 1,
              mb: 2,
              backgroundColor: "primary.main",
              color: "#ffffff",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1">
                {translator("People")}
              </Typography>
              <Link
                component={RouterLink}
                to="all"
                underline="none"
                color="inherit"
              >
                {translator("View all")}
              </Link>
            </Stack>
          </Paper>

          <Filters setFilters={setFilters} filters={filters} />
        </ConditionalRenderer>

        <Grid container spacing={3}>
          <ConditionalRenderer condition={filteredPeopleList?.length > 0}>
            {filteredPeopleList?.map((person: any, i: any) => (
              <Grid
                key={`people-card-${i}-${person.id}`}
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
              >
                <PersonCard
                  person={person}
                  isPrivate={isPrivate}
                  editPath={`/submission/people/${person?.id}/edit`}
                  onDelete={handleDelete}
                  disabled={deletingPeople}
                >
                  <ConditionalRenderer condition={isPrivate}>
                    <StatusWrapper>
                      <StatusTag
                        label={person?.statusLabel}
                        status={person?.status}
                      />
                    </StatusWrapper>
                  </ConditionalRenderer>
                </PersonCard>
              </Grid>
            ))}
          </ConditionalRenderer>
          <DataDisplayMessage data={filteredPeopleList} loading={loading} />
        </Grid>
      </Container>

      <ConditionalRenderer condition={isLoggedIn && isPrivate}>
        <Container sx={{ my: 3 }}>
          <CTAContainer sx={{ textAlign: "center" }}>
            <Typography variant="subtitle1" mb={2}>
              {translator("addNewItemToPlatform", {
                item: translator("confirmationItems.people"),
              })}
            </Typography>
            <Button
              component={RouterLink}
              to="new"
              sx={{
                px: 6,
                backgroundColor: "#ffffff",
                color: "primary.dark",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              }}
            >
              {`${translator("Add")} ${translator("People")}`}
            </Button>
          </CTAContainer>
        </Container>
      </ConditionalRenderer>

      <Listings
        title={`${translator("Latest")} ${translator("Actions")}`}
        data={filteredActionsList}
      />
      <Listings
        title={`${translator("Latest Positions")}`}
        data={filteredPositionsList}
      />
    </Box>
  );
}

export { PeopleHome };
